import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Heart,
  FileText,
  TrendingUp,
  AlertCircle,
  ListChecks,
  StickyNote,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  Trash2,
  Plus,
  Loader2,
  History,
  Shield,
  CornerDownRight,
  Square,
  CheckSquare,
  Timer,
  GraduationCap,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter } from '../../../../components/ui/Dialog';
import Button from '../../../../components/ui/Button';
import {
  getCheckInAgenda,
  saveCheckInAgenda,
  updatePreparationStatus,
  getContinuityPreview,
  getActionItems,
  saveActionItems,
  updateActionItemStatus,
  completeCheckIn,
  CheckInAgendaView,
  ContinuityPreviewView,
  PreparationStatus,
  CustomAgendaItemInput,
  ActionItemView,
  ActionItemInput,
  ActionItemStatus,
} from '../../../../api/PreformanceManagement/CheckIns';

interface CheckInDetailModalProps {
  checkInId: number;
  employeeName: string;
  managerName: string;
  scheduledDate: string;
  status: string;
  duration: number;
  completedDate?: string;
  managerNotes: string | null;
  preparationStatus: PreparationStatus;
  promptTemplates?: string[];
  onClose: () => void;
  onUpdate: () => void;
  onMarkAsCompleted?: () => void;
}

const CheckInDetailModal = ({
  checkInId,
  managerName,
  scheduledDate,
  status,
  duration,
  completedDate,
  managerNotes,
  preparationStatus: initialPrepStatus,
  promptTemplates = [],
  onClose,
  onUpdate,
  onMarkAsCompleted,
}: CheckInDetailModalProps) => {
  const { t } = useTranslation();
  const [agenda, setAgenda] = useState<CheckInAgendaView | null>(null);
  const [continuity, setContinuity] = useState<ContinuityPreviewView | null>(null);
  const [actionItemsList, setActionItemsList] = useState<ActionItemView[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [prepStatus, setPrepStatus] = useState(initialPrepStatus);

  // Editable state
  const [whatsBlocked, setWhatsBlocked] = useState('');
  const [whatsNeedsSupport, setWhatsNeedsSupport] = useState('');
  const [prepNotes, setPrepNotes] = useState('');
  const [customItems, setCustomItems] = useState<CustomAgendaItemInput[]>([]);
  const [newItemTitle, setNewItemTitle] = useState('');

  // Action items editable state
  const [editableActionItems, setEditableActionItems] = useState<ActionItemInput[]>([]);
  const [newActionTitle, setNewActionTitle] = useState('');
  const [newActionDueDate, setNewActionDueDate] = useState('');
  const [newActionIsDev, setNewActionIsDev] = useState(false);

  // Complete check-in form
  const [showCompleteForm, setShowCompleteForm] = useState(false);
  const [completeDuration, setCompleteDuration] = useState(duration);
  const [completeNotes, setCompleteNotes] = useState('');

  useEffect(() => {
    loadData();
  }, [checkInId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [agendaResult, continuityResult, actionItemsResult] = await Promise.allSettled([
        getCheckInAgenda(checkInId),
        getContinuityPreview(checkInId),
        getActionItems(checkInId),
      ]);

      if (agendaResult.status === 'fulfilled' && agendaResult.value.errors.length === 0) {
        const a = agendaResult.value.payload;
        setAgenda(a);
        setWhatsBlocked(a.whatsBlocked ?? '');
        setWhatsNeedsSupport(a.whatsNeedsSupport ?? '');
        setPrepNotes(a.prepNotes ?? '');
        setCustomItems(a.customItems.map((ci) => ({ id: ci.id, title: ci.title, notes: ci.notes, sortOrder: ci.sortOrder })));
        setPrepStatus(a.preparationStatus);
      }

      if (continuityResult.status === 'fulfilled' && continuityResult.value.errors.length === 0) {
        setContinuity(continuityResult.value.payload);
      }

      if (actionItemsResult.status === 'fulfilled' && actionItemsResult.value.errors.length === 0) {
        const items = actionItemsResult.value.payload.items;
        setActionItemsList(items);
        setEditableActionItems(items.map((ai) => ({
          id: ai.id,
          title: ai.title,
          assigneeId: ai.assigneeId,
          dueDate: ai.dueDate,
          status: ai.status,
          sortOrder: ai.sortOrder,
          isDevelopmentRelated: ai.isDevelopmentRelated,
        })));
      }
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const [agendaResult, actionResult] = await Promise.allSettled([
        saveCheckInAgenda({
          checkInId,
          customItems: customItems.map((item, i) => ({ ...item, sortOrder: i })),
          whatsBlocked,
          whatsNeedsSupport,
          prepNotes,
        }),
        saveActionItems({
          checkInId,
          items: editableActionItems.map((item, i) => ({ ...item, sortOrder: i })),
        }),
      ]);

      if (agendaResult.status === 'fulfilled' && agendaResult.value.errors.length === 0) {
        setAgenda(agendaResult.value.payload);
        setCustomItems(agendaResult.value.payload.customItems.map((ci) => ({ id: ci.id, title: ci.title, notes: ci.notes, sortOrder: ci.sortOrder })));
      }
      if (actionResult.status === 'fulfilled' && actionResult.value.errors.length === 0) {
        const items = actionResult.value.payload.items;
        setActionItemsList(items);
        setEditableActionItems(items.map((ai) => ({
          id: ai.id,
          title: ai.title,
          assigneeId: ai.assigneeId,
          dueDate: ai.dueDate,
          status: ai.status,
          sortOrder: ai.sortOrder,
          isDevelopmentRelated: ai.isDevelopmentRelated,
        })));
      }
      setIsEditing(false);
      onUpdate();
    } catch {
      /* ignore */
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePreparation = async () => {
    const newStatus = prepStatus === PreparationStatus.Prepared ? PreparationStatus.NotPrepared : PreparationStatus.Prepared;
    try {
      const result = await updatePreparationStatus({ checkInId, preparationStatus: newStatus });
      if (result.errors.length === 0) {
        setPrepStatus(newStatus);
        onUpdate();
      }
    } catch {
      /* ignore */
    }
  };

  const handleAddCustomItem = () => {
    if (newItemTitle.trim()) {
      setCustomItems((prev) => [...prev, { title: newItemTitle.trim(), sortOrder: prev.length }]);
      setNewItemTitle('');
    }
  };

  const handleRemoveCustomItem = (index: number) => {
    setCustomItems((prev) => prev.filter((_, i) => i !== index));
  };

  // Action item handlers
  const handleAddActionItem = () => {
    if (newActionTitle.trim()) {
      const newItem: ActionItemInput = {
        title: newActionTitle.trim(),
        dueDate: newActionDueDate || undefined,
        status: ActionItemStatus.Pending,
        sortOrder: editableActionItems.length,
        isDevelopmentRelated: newActionIsDev,
      };
      setEditableActionItems((prev) => [...prev, newItem]);
      // Also add to display list for immediate feedback
      setActionItemsList((prev) => [
        ...prev,
        {
          id: -(prev.length + 1), // temp negative id
          checkInId,
          title: newItem.title,
          dueDate: newItem.dueDate,
          status: ActionItemStatus.Pending,
          sortOrder: newItem.sortOrder,
          carriedForward: false,
          isDevelopmentRelated: newActionIsDev,
        } as ActionItemView,
      ]);
      setNewActionTitle('');
      setNewActionDueDate('');
      setNewActionIsDev(false);
    }
  };

  const handleRemoveActionItem = (index: number) => {
    setEditableActionItems((prev) => prev.filter((_, i) => i !== index));
    setActionItemsList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleToggleActionItemStatus = async (item: ActionItemView) => {
    if (item.id < 0) return; // unsaved item, can't toggle via API
    const newStatus = item.status === ActionItemStatus.Completed ? ActionItemStatus.Pending : ActionItemStatus.Completed;
    try {
      const result = await updateActionItemStatus({ actionItemId: item.id, status: newStatus });
      if (result.errors.length === 0) {
        setActionItemsList((prev) =>
          prev.map((ai) => (ai.id === item.id ? { ...ai, status: newStatus, completedAt: result.payload.completedAt } : ai)),
        );
        setEditableActionItems((prev) =>
          prev.map((ai) => (ai.id === item.id ? { ...ai, status: newStatus } : ai)),
        );
      }
    } catch {
      /* ignore */
    }
  };

  const handleCompleteCheckIn = async () => {
    setCompleting(true);
    try {
      const result = await completeCheckIn({
        checkInId,
        actualDurationMinutes: completeDuration,
        managerNotes: completeNotes || undefined,
      });
      if (result.errors.length === 0) {
        setShowCompleteForm(false);
        if (onMarkAsCompleted) onMarkAsCompleted();
      }
    } catch {
      /* ignore */
    } finally {
      setCompleting(false);
    }
  };

  const scheduledDateObj = new Date(scheduledDate);
  const completedDateObj = completedDate ? new Date(completedDate) : null;

  const getStatusBadge = (s: string) => {
    const badges: Record<string, { label: string; color: string; icon: typeof Calendar }> = {
      scheduled: { label: t('checkins.scheduled'), color: 'bg-primary/10 text-primary', icon: Calendar },
      completed: { label: t('checkins.completed'), color: 'bg-success/10 text-success', icon: CheckCircle },
      missed: { label: t('checkins.missed'), color: 'bg-destructive/10 text-destructive', icon: XCircle },
      rescheduled: { label: t('checkins.scheduled'), color: 'bg-warning/10 text-warning', icon: Clock },
    };
    return badges[s] || badges.scheduled;
  };

  const statusBadge = getStatusBadge(status);

  const completedActionCount = actionItemsList.filter((ai) => ai.status === ActionItemStatus.Completed).length;
  const totalActionCount = actionItemsList.length;

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="xl" hideCloseButton>
        {/* Header */}
        <DialogHeader className="bg-primary/5 p-6 border-b border-border">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4 rtl:space-x-reverse">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-medium text-xl">
                {managerName
                  .split(' ')
                  .map((n) => n[0])
                  .join('')}
              </div>
              <div>
                <div className="flex items-center space-x-2 rtl:space-x-reverse mb-1">
                  <h2 className="text-2xl font-bold text-foreground">
                    {t('checkins.checkInWith')} {managerName}
                  </h2>
                  <div className={`flex items-center space-x-1 rtl:space-x-reverse px-2 py-1 rounded-full text-xs font-medium ${statusBadge.color}`}>
                    <statusBadge.icon className="w-3 h-3" />
                    <span>{statusBadge.label}</span>
                  </div>
                </div>
                <p className="text-muted-foreground">
                  {scheduledDateObj.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  {' · '}{duration} {t('checkins.min')}
                </p>
                {completedDateObj && (
                  <p className="text-sm text-success mt-1">
                    {t('checkins.completedOn')} {completedDateObj.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                )}
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-card/50 rounded-lg transition-colors" data-testid="close-modal-btn">
              <X className="w-6 h-6 text-muted-foreground" />
            </button>
          </div>

          {/* Preparation Status Toggle */}
          {status === 'scheduled' && (
            <div className="mt-4 flex items-center justify-between p-3 bg-card/70 rounded-lg border border-primary/20">
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                <Shield className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-foreground">{t('checkins.preparationStatus')}</span>
              </div>
              <button
                onClick={handleTogglePreparation}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  prepStatus === PreparationStatus.Prepared
                    ? 'bg-success/10 text-success border border-success/30'
                    : 'bg-muted text-muted-foreground border border-border hover:bg-warning/10 hover:text-warning hover:border-warning/30'
                }`}
                data-testid="prep-status-toggle"
              >
                {prepStatus === PreparationStatus.Prepared ? t('checkins.prepared') : t('checkins.notPrepared')}
              </button>
            </div>
          )}

          {/* Psychological Contract Reminder */}
          <div className="mt-3 p-3 bg-card/70 rounded-lg border border-primary/20">
            <div className="flex items-start space-x-2 rtl:space-x-reverse">
              <Heart className="w-4 h-4 text-primary mt-0.5" />
              <p className="text-xs text-foreground leading-relaxed">
                <strong>{t('checkins.remember')}:</strong> {t('checkins.psychContractReminder')}
              </p>
            </div>
          </div>
        </DialogHeader>

        {/* Content */}
        <DialogBody className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : (
            <>
              {/* Continuity Preview */}
              {continuity && continuity.lastCheckInDate && (
                <div className="bg-warning/10 border border-warning/20 rounded-lg p-4" data-testid="continuity-preview">
                  <h4 className="text-sm font-semibold text-warning mb-2 flex items-center space-x-2 rtl:space-x-reverse">
                    <History className="w-4 h-4" />
                    <span>{t('checkins.continuityPreview')}</span>
                  </h4>
                  <p className="text-xs text-warning mb-2">
                    {t('checkins.lastCheckIn')}: {new Date(continuity.lastCheckInDate).toLocaleDateString()}
                  </p>
                  {continuity.lastManagerNotes && (
                    <p className="text-xs text-warning mb-2 line-clamp-2">
                      <strong>{t('checkins.lastNotes')}:</strong> {continuity.lastManagerNotes}
                    </p>
                  )}
                  {continuity.lastWhatsBlocked && (
                    <p className="text-xs text-warning mb-2">
                      <strong>{t('checkins.previousBlockers')}:</strong> {continuity.lastWhatsBlocked}
                    </p>
                  )}
                  {continuity.pendingActionItems.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-warning mb-1">{t('checkins.pendingItems')}:</p>
                      <ul className="space-y-1">
                        {continuity.pendingActionItems.map((item, i) => (
                          <li key={i} className="text-xs text-warning flex items-start space-x-1 rtl:space-x-reverse">
                            <span className="text-warning mt-0.5">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Auto-generated Agenda Section */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2 rtl:space-x-reverse">
                    <FileText className="w-5 h-5" />
                    <span>{t('checkins.autoGeneratedAgenda')}</span>
                  </h3>
                  {!isEditing && status === 'scheduled' && (
                    <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} iconName="Edit" data-testid="edit-agenda-btn">
                      {t('checkins.edit')}
                    </Button>
                  )}
                </div>

                {/* Since Last Check-in (auto-populated) */}
                <div className="bg-card border border-border rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center space-x-2 rtl:space-x-reverse">
                    <TrendingUp className="w-4 h-4 text-success" />
                    <span>{t('checkins.sinceLastCheckIn')}</span>
                  </h4>
                  {agenda && agenda.autoItems.length > 0 ? (
                    <div className="space-y-2">
                      {agenda.autoItems.map((item) => (
                        <div key={item.id} className="flex items-start space-x-3 rtl:space-x-reverse p-2 bg-muted rounded">
                          <div
                            className={`w-2 h-2 rounded-full mt-1.5 ${
                              item.type === 'goal' ? 'bg-success' : item.type === 'feedback' ? 'bg-error' : 'bg-primary'
                            }`}
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-foreground">{item.title}</p>
                            <div className="flex items-center space-x-2 rtl:space-x-reverse mt-1">
                              <span className="text-xs text-muted-foreground">{item.source}</span>
                              {item.progress !== undefined && item.progress !== null && (
                                <span className="text-xs text-success font-medium">{item.progress}% {t('checkins.complete')}</span>
                              )}
                              {item.from && <span className="text-xs text-muted-foreground">{t('checkins.from')} {item.from}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">{t('checkins.noItems')}</p>
                  )}
                </div>

                {/* Custom Agenda Items */}
                <div className="bg-card border border-border rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center space-x-2 rtl:space-x-reverse">
                    <ListChecks className="w-4 h-4 text-primary" />
                    <span>{t('checkins.customAgendaItems')}</span>
                  </h4>
                  {customItems.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {customItems.map((item, index) => (
                        <div key={item.id ?? `new-${index}`} className="flex items-center space-x-2 rtl:space-x-reverse p-2 bg-muted rounded">
                          <span className="text-sm text-foreground flex-1">{item.title}</span>
                          {isEditing && (
                            <button onClick={() => handleRemoveCustomItem(index)} className="p-1 hover:bg-muted rounded">
                              <Trash2 className="w-3 h-3 text-destructive" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {isEditing && (
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <input
                        type="text"
                        placeholder={t('checkins.addCustomItemPlaceholder')}
                        value={newItemTitle}
                        onChange={(e) => setNewItemTitle(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddCustomItem()}
                        className="flex-1 px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                        data-testid="new-custom-item-input"
                      />
                      <button onClick={handleAddCustomItem} className="p-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20" data-testid="add-custom-item-btn">
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  {!isEditing && customItems.length === 0 && (
                    <p className="text-sm text-muted-foreground">{t('checkins.noCustomItems')}</p>
                  )}
                </div>

                {/* Action Items Section (Story 7.3) */}
                <div className="bg-card border border-border rounded-lg p-4 mb-4" data-testid="action-items-section">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-foreground flex items-center space-x-2 rtl:space-x-reverse">
                      <CheckSquare className="w-4 h-4 text-primary" />
                      <span>{t('checkins.actionItems')}</span>
                      {totalActionCount > 0 && (
                        <span className="text-xs text-muted-foreground font-normal">
                          ({completedActionCount}/{totalActionCount})
                        </span>
                      )}
                    </h4>
                  </div>

                  {/* Action Items List */}
                  {actionItemsList.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {actionItemsList.map((item, index) => (
                        <div key={item.id ?? `action-${index}`} className="flex items-start space-x-2 rtl:space-x-reverse p-2 bg-muted rounded group">
                          <button
                            onClick={() => handleToggleActionItemStatus(item)}
                            className="mt-0.5 flex-shrink-0"
                            data-testid={`action-item-toggle-${item.id}`}
                          >
                            {item.status === ActionItemStatus.Completed ? (
                              <CheckSquare className="w-4 h-4 text-success" />
                            ) : (
                              <Square className="w-4 h-4 text-muted-foreground group-hover:text-primary" />
                            )}
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${item.status === ActionItemStatus.Completed ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                              {item.title}
                            </p>
                            <div className="flex items-center space-x-2 rtl:space-x-reverse mt-0.5 flex-wrap gap-1">
                              {item.isDevelopmentRelated && (
                                <span className="inline-flex items-center gap-0.5 text-[10px] text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">
                                  <GraduationCap className="w-2.5 h-2.5" />
                                  {t('checkins.developmentRelated')}
                                </span>
                              )}
                              {item.carriedForward && (
                                <span className="inline-flex items-center gap-0.5 text-[10px] text-warning bg-warning/10 px-1.5 py-0.5 rounded-full">
                                  <CornerDownRight className="w-2.5 h-2.5" />
                                  {t('checkins.carriedForward')}
                                </span>
                              )}
                              {item.assigneeName && (
                                <span className="text-[10px] text-muted-foreground">{item.assigneeName}</span>
                              )}
                              {item.dueDate && (
                                <span className="text-[10px] text-muted-foreground">
                                  {t('checkins.due')}: {new Date(item.dueDate).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                          {isEditing && (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditableActionItems((prev) =>
                                    prev.map((ai, i) => (i === index ? { ...ai, isDevelopmentRelated: !ai.isDevelopmentRelated } : ai)),
                                  );
                                  setActionItemsList((prev) =>
                                    prev.map((ai, i) => (i === index ? { ...ai, isDevelopmentRelated: !ai.isDevelopmentRelated } : ai)),
                                  );
                                }}
                                title={t('checkins.toggleDevelopmentRelated')}
                                className={`p-1 rounded flex-shrink-0 transition-colors ${
                                  editableActionItems[index]?.isDevelopmentRelated
                                    ? 'text-primary bg-primary/10'
                                    : 'text-muted-foreground hover:text-primary'
                                }`}
                                data-testid={`action-item-dev-toggle-${item.id}`}
                              >
                                <GraduationCap className="w-3 h-3" />
                              </button>
                              <button onClick={() => handleRemoveActionItem(index)} className="p-1 hover:bg-muted rounded flex-shrink-0">
                                <Trash2 className="w-3 h-3 text-destructive" />
                              </button>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Action Item */}
                  {(isEditing || status === 'scheduled') && (
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        <input
                          type="text"
                          placeholder={t('checkins.actionItemPlaceholder')}
                          value={newActionTitle}
                          onChange={(e) => setNewActionTitle(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleAddActionItem()}
                          className="flex-1 px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                          data-testid="new-action-item-input"
                        />
                        <input
                          type="date"
                          value={newActionDueDate}
                          onChange={(e) => setNewActionDueDate(e.target.value)}
                          className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                          data-testid="new-action-due-date"
                        />
                        <button
                          type="button"
                          onClick={() => setNewActionIsDev((v) => !v)}
                          title={t('checkins.toggleDevelopmentRelated')}
                          className={`p-2 rounded-lg transition-colors ${
                            newActionIsDev
                              ? 'bg-primary/20 text-primary'
                              : 'bg-muted text-muted-foreground hover:bg-muted'
                          }`}
                          data-testid="new-action-dev-toggle"
                        >
                          <GraduationCap className="w-4 h-4" />
                        </button>
                        <button onClick={handleAddActionItem} className="p-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20" data-testid="add-action-item-btn">
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}

                  {!isEditing && status !== 'scheduled' && actionItemsList.length === 0 && (
                    <p className="text-sm text-muted-foreground">{t('checkins.noActionItems')}</p>
                  )}
                </div>

                {/* What's Blocked */}
                <div className="bg-card border border-border rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center space-x-2 rtl:space-x-reverse">
                    <AlertCircle className="w-4 h-4 text-warning" />
                    <span>{t('checkins.whatsBlocked')}</span>
                  </h4>
                  {isEditing ? (
                    <textarea
                      value={whatsBlocked}
                      onChange={(e) => setWhatsBlocked(e.target.value)}
                      className="w-full p-3 border border-border rounded-lg resize-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary text-sm"
                      rows={3}
                      data-testid="whats-blocked-input"
                    />
                  ) : (
                    <p className="text-sm text-foreground">{whatsBlocked || t('checkins.noBlockers')}</p>
                  )}
                </div>

                {/* What Needs Support */}
                <div className="bg-card border border-border rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center space-x-2 rtl:space-x-reverse">
                    <Heart className="w-4 h-4 text-primary" />
                    <span>{t('checkins.whatsNeedsSupport')}</span>
                  </h4>
                  {isEditing ? (
                    <textarea
                      value={whatsNeedsSupport}
                      onChange={(e) => setWhatsNeedsSupport(e.target.value)}
                      className="w-full p-3 border border-border rounded-lg resize-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary text-sm"
                      rows={3}
                      data-testid="whats-needs-support-input"
                    />
                  ) : (
                    <p className="text-sm text-foreground">{whatsNeedsSupport || t('checkins.noSupportNeeds')}</p>
                  )}
                </div>
              </div>

              {/* Prep Notes Section */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2 rtl:space-x-reverse">
                  <StickyNote className="w-5 h-5" />
                  <span>{t('checkins.prepNotes')}</span>
                </h3>

                {/* Prompt Templates from Schedule */}
                {promptTemplates.length > 0 && (isEditing || status === 'scheduled') && (
                  <div className="mb-3 p-3 bg-primary/5 border border-primary/20 rounded-lg" data-testid="prompt-templates">
                    <p className="text-xs font-medium text-primary mb-2">{t('checkins.promptTemplatesLabel')}</p>
                    <div className="flex flex-wrap gap-2">
                      {promptTemplates.map((prompt, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setPrepNotes((prev) => (prev ? `${prev}\n${prompt}` : prompt))}
                          className="text-xs px-2.5 py-1.5 bg-card border border-border rounded-full text-foreground hover:bg-primary/10 hover:border-primary/30 transition-colors"
                          data-testid={`prompt-template-${i}`}
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {isEditing || status === 'scheduled' ? (
                  <textarea
                    value={prepNotes}
                    onChange={(e) => setPrepNotes(e.target.value)}
                    placeholder={t('checkins.prepNotesPlaceholder')}
                    className="w-full p-4 border border-border rounded-lg resize-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary text-sm"
                    rows={4}
                    data-testid="prep-notes-input"
                  />
                ) : (
                  <div className="bg-card border border-border rounded-lg p-4">
                    <p className="text-sm text-foreground leading-relaxed">{prepNotes || t('checkins.noPrepNotes')}</p>
                  </div>
                )}
              </div>

              {/* Manager Notes (read-only display) */}
              {managerNotes && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2 rtl:space-x-reverse">
                    <StickyNote className="w-5 h-5" />
                    <span>{t('checkins.softNotes')}</span>
                  </h3>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <p className="text-sm text-foreground leading-relaxed">{managerNotes}</p>
                  </div>
                </div>
              )}

              {/* Complete Check-In Form */}
              {showCompleteForm && (
                <div className="bg-success/10 border border-success/20 rounded-lg p-4" data-testid="complete-checkin-form">
                  <h4 className="text-sm font-semibold text-success mb-3 flex items-center space-x-2 rtl:space-x-reverse">
                    <CheckCircle className="w-4 h-4" />
                    <span>{t('checkins.completeCheckIn')}</span>
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('checkins.actualDuration')}</label>
                      <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        <Timer className="w-4 h-4 text-muted-foreground" />
                        <input
                          type="number"
                          min={1}
                          max={480}
                          value={completeDuration}
                          onChange={(e) => setCompleteDuration(Number(e.target.value))}
                          className="w-24 px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                          data-testid="complete-duration-input"
                        />
                        <span className="text-sm text-muted-foreground">{t('checkins.minutes')}</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('checkins.completionNotes')}</label>
                      <textarea
                        value={completeNotes}
                        onChange={(e) => setCompleteNotes(e.target.value)}
                        placeholder={t('checkins.completionNotesPlaceholder')}
                        className="w-full p-3 border border-border rounded-lg resize-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary text-sm"
                        rows={3}
                        data-testid="complete-notes-input"
                      />
                    </div>
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <Button
                        variant="success"
                        onClick={handleCompleteCheckIn}
                        disabled={completing}
                        loading={completing}
                        iconName="CheckCircle"
                        data-testid="confirm-complete-btn"
                      >
                        {t('checkins.confirmComplete')}
                      </Button>
                      <Button variant="outline" onClick={() => setShowCompleteForm(false)}>
                        {t('common.cancel')}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogBody>

        {/* Footer Actions */}
        <DialogFooter className="justify-between">
          <div>
            {status === 'scheduled' && !showCompleteForm && (
              <Button
                variant="success"
                onClick={() => setShowCompleteForm(true)}
                iconName="CheckCircle"
                data-testid="mark-completed-btn"
              >
                {t('checkins.markAsCompleted')}
              </Button>
            )}
          </div>
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            <Button variant="outline" onClick={onClose}>
              {t('checkins.close')}
            </Button>
            {(isEditing || status === 'scheduled') && (
              <Button
                onClick={handleSave}
                disabled={saving}
                loading={saving}
                iconName="Save"
                data-testid="save-changes-btn"
              >
                {t('checkins.saveChanges')}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CheckInDetailModal;
